import { useState } from "react";
import {
  PanelShell, Badge, StatChip, Button, ErrorState, ApiResourceView,
  TextField, PillToggleGroup, CheckboxRow, tokens,
  DJSEMApiClient, useApiResource, useApiAction,
  STEM_MODE_OPTIONS, checkStemSourceEligibility, canCreateStemJob, stemJobStatusTone, stemJobStatusLabel,
  STEM_RIGHTS_CONFIRMATION_COPY, type DJSEMStemSeparationModeUI, type StemJobStatusUI,
  stemQualityLabel, stemApprovalTone, stemApprovalLabel, stemApprovalActionsFor, type StemApprovalStateUI,
  STEM_EXPORT_TYPES, stemExportTypeLabel, type StemExportTypeUI,
  stemStudioAvailability,
} from "../index";
import type { DJSEMPlatform } from "../platform";
import { toBadgeTone } from "./shared";

interface StemJobUI { id: string; status: StemJobStatusUI; providerId?: string; modelId?: string; progress?: number; warnings?: string[]; errors?: string[]; }

const SOURCE_PLATFORMS = ["local", "upload", "spotify", "soundcloud", "youtube"] as const;

/**
 * Stem Studio is desktop-only regardless of plan (ARTIST_IDE_GUARDRAIL_V3.md §4/§5 — stems are
 * listed under "Desktop app = everything in web plus... stems", and §5's desktop/pro-only list).
 * A thin, hookless wrapper so the platform branch can early-return before StemStudioForm's hooks
 * run — same shape as VideoGenerationModule/VideoGenerationForm in ./VideoGeneration.tsx.
 *
 * @param client - The API client passed to the stem separation form on desktop.
 * @param platform - The current platform; determines whether the real form or the
 *   desktop-only notice is shown.
 * @returns A desktop-only notice on web, or the full Stem Studio form.
 */
export function StemStudioModule({ client, platform }: { client: DJSEMApiClient; platform: DJSEMPlatform }) {
  const availability = stemStudioAvailability(platform.kind);
  if (!availability.available) {
    return (
      <PanelShell title="Stem Studio — AI stem separation">
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 560 }}>
          <div style={{ fontSize: tokens.text.lg, color: tokens.color.text }}>
            Stem separation runs in the DJSEM desktop app.
          </div>
          <p style={{ margin: 0, color: tokens.color.textDim, fontSize: tokens.text.md, lineHeight: 1.55 }}>
            The browser app covers library, sets, visuals, brand, release assets, campaigns and
            gigs. {availability.reason} Everything you prepare here syncs to desktop automatically.
          </p>
          <div style={{ marginTop: 4 }}>
            <Badge tone="neutral">Desktop feature</Badge>
          </div>
        </div>
      </PanelShell>
    );
  }
  return <StemStudioForm client={client} />;
}

/**
 * AI Stem Separation Studio (Prompt 135/19A, extended by 140/141 model registry). Wizard:
 * source/eligibility → rights confirmation (active checkbox, no pre-tick) → mode → estimate →
 * confirm & run. Review/approve of the resulting stems is prompt 136's screen (a follow-on —
 * this module links to "Open review" once a job completes, but does not itself replace it).
 */
function StemStudioForm({ client }: { client: DJSEMApiClient }) {
  const [trackId, setTrackId] = useState("");
  const [sourceAudioRef, setSourceAudioRef] = useState("");
  const [sourcePlatform, setSourcePlatform] = useState<(typeof SOURCE_PLATFORMS)[number]>("local");
  const [hasLocalFile, setHasLocalFile] = useState(true);
  const [mode, setMode] = useState<DJSEMStemSeparationModeUI>("four_stem_drums_bass_vocals_other");
  const [rightsChecked, setRightsChecked] = useState(false);
  const [creditChecked, setCreditChecked] = useState(false);
  const [lastJob, setLastJob] = useState<StemJobUI | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const eligibility = checkStemSourceEligibility({ hasLocalFile, sourcePlatform, fileFormatSupported: true, rightsState: "unknown" });
  const canRun = eligibility.eligibility === "eligible" && canCreateStemJob({ rightsChecked, creditChecked });

  const estimate = useApiAction((input: Parameters<DJSEMApiClient["estimateStemSeparation"]>[0]) => client.estimateStemSeparation(input));
  const runJob = useApiAction(async () => {
    const reqRes = await client.createStemSeparationRequest({
      trackId, sourceAudioRef, requestedMode: mode, outputFormat: "wav", qualityMode: "standard",
      userConfirmedRightsWarning: rightsChecked, userConfirmedCreditUsage: creditChecked,
    });
    if (!reqRes.ok) return reqRes;
    const jobRes = await client.createStemSeparationJob(reqRes.data.requestId);
    if (jobRes.ok) { setLastJob(jobRes.data.job as StemJobUI); setSelectedJobId((jobRes.data.job as StemJobUI).id); }
    return jobRes;
  });

  const jobsList = useApiResource(() => client.listStemSeparationJobs(), [], (d) => (d.jobs?.length ?? 0) === 0);

  return (
    <>
      <PanelShell title="Stem Studio — separate a track">
        <div style={{ fontSize: 12, color: tokens.color.accent, marginBottom: 10, fontStyle: "italic" }}>
          AI suggests. Artist decides. DJSEM explains. You approve.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 480 }}>
          <TextField placeholder="Track ID" value={trackId} onChange={setTrackId} />
          <TextField placeholder="Source audio reference (local file path or upload ref)" value={sourceAudioRef} onChange={setSourceAudioRef} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: tokens.color.textDim }}>Source:</span>
            <PillToggleGroup options={SOURCE_PLATFORMS} value={sourcePlatform} onChange={setSourcePlatform} />
            <label style={{ fontSize: 11, color: tokens.color.textDim, marginLeft: 8 }}>
              <input type="checkbox" checked={hasLocalFile} onChange={(e: { target: { checked: boolean } }) => setHasLocalFile(e.target.checked)} /> have a local file
            </label>
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          {eligibility.eligibility === "eligible"
            ? <Badge tone="ok">eligible</Badge>
            : <Badge tone="warn">{eligibility.eligibility.replace(/_/g, " ")}</Badge>}
          <div style={{ fontSize: 11, color: tokens.color.textDim, marginTop: 4 }}>{eligibility.reason}</div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: tokens.color.text, marginBottom: 6 }}>Separation mode</div>
          <PillToggleGroup
            options={STEM_MODE_OPTIONS.map((o) => o.mode)} value={mode} onChange={setMode}
            labelFor={(m) => STEM_MODE_OPTIONS.find((o) => o.mode === m)?.label ?? m}
            titleFor={(m) => STEM_MODE_OPTIONS.find((o) => o.mode === m)?.bestFor}
          />
        </div>

        <div style={{ marginTop: 14, padding: 10, background: tokens.color.surface2, borderRadius: 4 }}>
          {STEM_RIGHTS_CONFIRMATION_COPY.map((line, i) => (
            <div key={i} style={{ fontSize: 11, color: tokens.color.textDim, marginBottom: 4 }}>{line}</div>
          ))}
          <CheckboxRow checked={rightsChecked} onChange={setRightsChecked}>I confirm I own or am licensed to use this audio.</CheckboxRow>
          <CheckboxRow checked={creditChecked} onChange={setCreditChecked}>I understand this job consumes credits.</CheckboxRow>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <Button
            disabled={eligibility.eligibility !== "eligible" || !trackId || !sourceAudioRef || estimate.pending}
            onClick={() => void estimate.run({ trackId, sourceAudioRef, requestedMode: mode })}
          >{estimate.pending ? "Estimating…" : "Estimate"}</Button>
          <Button disabled={!canRun || !trackId || !sourceAudioRef || runJob.pending} onClick={() => void runJob.run()}>
            {runJob.pending ? "Running…" : "Confirm & run"}
          </Button>
        </div>

        {estimate.view?.state === "ready" && estimate.view.data ? (
          <div style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <StatChip label="Credits" value={String(estimate.view.data.estimatedCreditCost)} />
            {estimate.view.data.resolvedModelId ? <StatChip label="Model" value={estimate.view.data.resolvedModelId} tone="accent" /> : null}
            {estimate.view.data.blockers.length > 0 ? <Badge tone="warn">{estimate.view.data.blockers[0]}</Badge> : null}
          </div>
        ) : null}
        {estimate.view?.state === "error" ? <ErrorState detail={estimate.view.message} /> : null}

        {lastJob ? (
          <div style={{ marginTop: 12 }}>
            <Badge tone={toBadgeTone(stemJobStatusTone(lastJob.status))}>{stemJobStatusLabel(lastJob.status)}</Badge>
            <span style={{ fontSize: 11, color: tokens.color.textDim, marginLeft: 8 }}>job {lastJob.id}</span>
            {lastJob.status === "completed" ? (
              <button onClick={() => setSelectedJobId(lastJob.id)} style={{ fontSize: 11, color: tokens.color.accent, marginLeft: 8, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                Open review to approve stems
              </button>
            ) : null}
          </div>
        ) : null}
      </PanelShell>

      <PanelShell title="Recent stem jobs" style={{ marginTop: 12 }}>
        <ApiResourceView
          resource={jobsList}
          empty={{ title: "No stem jobs yet", detail: "Run your first separation above." }}
          render={(data) => (
            <>
              {((data.jobs ?? []) as unknown as StemJobUI[]).map((j) => (
                <div key={j.id} onClick={() => setSelectedJobId(j.id)} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", cursor: "pointer" }}>
                  <span>{j.id}{j.modelId ? <span style={{ fontSize: 10, opacity: 0.6 }}> · {j.modelId}</span> : null}</span>
                  <Badge tone={toBadgeTone(stemJobStatusTone(j.status))}>{stemJobStatusLabel(j.status)}</Badge>
                </div>
              ))}
            </>
          )}
        />
      </PanelShell>

      {selectedJobId ? <StemReviewPanel client={client} jobId={selectedJobId} /> : null}
    </>
  );
}

interface StemOutputUI {
  id: string; role: string; label: string; confidence: number; format: string; durationMs?: number;
  providerId: string; modelId?: string; warnings: string[]; approvalState: string; reviewNote?: string;
}

/**
 * Stem Review workspace (Prompt 136). Per-stem quality state in plain language, approve/reject/
 * regenerate/archive actions, and storage/provenance summary. Not a DAW — review, prep, and
 * approval only (prompt 136 §8).
 */
function StemReviewPanel({ client, jobId }: { client: DJSEMApiClient; jobId: string }) {
  const job = useApiResource(() => client.getStemSeparationJob(jobId), [jobId]);
  const approve = useApiAction((outputId: string, state: string) => client.setStemOutputApproval(outputId, state));
  const [exportType, setExportType] = useState<StemExportTypeUI>(STEM_EXPORT_TYPES[0]);
  const exportCheck = useApiAction((jId: string, ty: string) => client.checkStemExport(jId, ty));

  return (
    <ApiResourceView
      resource={job}
      empty={{ title: "No stems to review yet", detail: "This job hasn't produced outputs — check its status above." }}
      render={(data) => {
        const outputs = ((data.outputs ?? []) as unknown as StemOutputUI[]);
        if (outputs.length === 0) {
          return <PanelShell title="Stem review" style={{ marginTop: 12 }}><Badge tone="neutral">No stems to review yet</Badge></PanelShell>;
        }
        return (
          <>
            <PanelShell title={`Stem review — ${jobId}`} style={{ marginTop: 12 }}>
              {outputs.map((o) => {
                const quality = stemQualityLabel(o.confidence, o.warnings.length > 0);
                const approvalState = (o.approvalState as StemApprovalStateUI) ?? "pending_review";
                const actions = stemApprovalActionsFor(approvalState);
                return (
                  <div key={o.id} style={{ padding: "8px 0", borderBottom: `1px solid ${tokens.color.border}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13 }}>{o.label} <span style={{ fontSize: 10, opacity: 0.6 }}>({o.role})</span></span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Badge tone={quality === "Excellent" || quality === "Good" ? "ok" : quality === "Failed" ? "warn" : "neutral"}>{quality}</Badge>
                        <Badge tone={toBadgeTone(stemApprovalTone(approvalState))}>{stemApprovalLabel(approvalState)}</Badge>
                      </div>
                    </div>
                    {o.warnings.length > 0 ? (
                      <div style={{ fontSize: 11, color: "#e8b24a", marginTop: 4 }}>{o.warnings.join(" · ")}</div>
                    ) : null}
                    <div style={{ fontSize: 11, color: tokens.color.textDim, marginTop: 4 }}>
                      {o.format.toUpperCase()}{o.durationMs ? ` · ${(o.durationMs / 1000).toFixed(1)}s` : ""} · provider {o.providerId}{o.modelId ? ` · model ${o.modelId}` : ""}
                    </div>
                    {actions.length > 0 ? (
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        {actions.map((a) => (
                          <button key={a} disabled={approve.pending} onClick={() => void approve.run(o.id, a === "approve" ? "approved" : a === "reject" ? "rejected" : a === "regenerate" ? "needs_regeneration" : a === "restore" ? "pending_review" : "archived").then(job.refetch)}
                            style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, cursor: "pointer", background: "transparent", color: tokens.color.accent, border: `1px solid ${tokens.color.accentDim}` }}>
                            {a}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </PanelShell>

            <PanelShell title="Export check (Prompt 139)" style={{ marginTop: 12 }}>
              <p style={{ margin: "0 0 10px", color: tokens.color.textDim, fontSize: 12 }}>
                Only approved stems are considered. Anything not yet approved blocks the whole export.
              </p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select value={exportType} onChange={(e: { target: { value: string } }) => setExportType(e.target.value as StemExportTypeUI)}
                  style={{ padding: 6, fontSize: 12, background: tokens.color.bg, color: tokens.color.text, border: `1px solid ${tokens.color.border}`, borderRadius: 4 }}>
                  {STEM_EXPORT_TYPES.map((ty) => <option key={ty} value={ty}>{stemExportTypeLabel(ty)}</option>)}
                </select>
                <Button disabled={exportCheck.pending} onClick={() => void exportCheck.run(jobId, exportType)}>
                  {exportCheck.pending ? "Checking…" : "Check export"}
                </Button>
              </div>
              {exportCheck.view?.state === "ready" && exportCheck.view.data ? (
                <div style={{ marginTop: 10 }}>
                  <Badge tone={exportCheck.view.data.allAllowed ? "ok" : "warn"}>
                    {exportCheck.view.data.allAllowed ? "allowed" : "blocked"}
                  </Badge>
                  {exportCheck.view.data.excludedCount > 0 ? (
                    <span style={{ fontSize: 11, color: tokens.color.textDim, marginLeft: 8 }}>
                      {exportCheck.view.data.excludedCount} rejected/archived stem{exportCheck.view.data.excludedCount === 1 ? "" : "s"} excluded from this export
                    </span>
                  ) : null}
                  {exportCheck.view.data.blockers.map((b, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#e8b24a", marginTop: 4 }}>{b}</div>
                  ))}
                </div>
              ) : null}
              {exportCheck.view?.state === "error" ? <ErrorState detail={exportCheck.view.message} /> : null}
            </PanelShell>
          </>
        );
      }}
    />
  );
}
